/**
 * GoodGuardian — Import from Platform
 *
 * Step-by-step wizard to import communication history and calendar events
 * from OFW, TalkingParents, or other co-parenting platforms via PDF export.
 * Parsed records are saved to both the Communications Log and Calendar.
 */
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { useApp } from '@/context/AppContext'
import { data as dataApi } from '@/utils/auth'

const PLATFORMS = [
  {
    id: 'ofw',
    name: 'Our Family Wizard',
    emoji: '📗',
    instructions: [
      'Log in to ourfamilywizard.com on a desktop browser.',
      'Navigate to the "Message Board" section.',
      'Click the "Print" or "Export" button at the top right.',
      'Select "Export to PDF" and choose your desired date range.',
      'Click "Export" — your browser will download the PDF.',
      'Upload that PDF below.',
    ],
  },
  {
    id: 'talkingparents',
    name: 'TalkingParents',
    emoji: '💬',
    instructions: [
      'Log in to talkingparents.com.',
      'Tap or click "Messaging" in the navigation.',
      'Open the conversation you want to export.',
      'Tap the three-dot menu (⋮) in the top right corner.',
      'Select "Download PDF" and save it to your device.',
      'Upload that PDF below.',
    ],
  },
  {
    id: 'other',
    name: 'Other Platform',
    emoji: '📄',
    instructions: [
      'Export a PDF of your message or communication history from your platform.',
      'Make sure the PDF includes dates, sender names, and message content.',
      'Upload the PDF below — GoodGuardian will extract the records automatically.',
    ],
  },
]

const COMM_TONE_META = {
  neutral:     { label: 'Neutral',     bg: 'bg-slate-100',  text: 'text-slate-700'  },
  cooperative: { label: 'Cooperative', bg: 'bg-green-100',  text: 'text-green-700'  },
  hostile:     { label: 'Hostile',     bg: 'bg-red-100',    text: 'text-red-700'    },
  concerning:  { label: 'Concerning',  bg: 'bg-orange-100', text: 'text-orange-700' },
}

const CAL_TYPE_META = {
  'custody-exchange': { label: 'Custody Exchange', bg: 'bg-blue-100',   text: 'text-blue-700'   },
  'appointment':      { label: 'Appointment',      bg: 'bg-purple-100', text: 'text-purple-700' },
  'school':           { label: 'School Event',     bg: 'bg-green-100',  text: 'text-green-700'  },
  'court':            { label: 'Court Hearing',    bg: 'bg-amber-100',  text: 'text-amber-700'  },
  'compliance':       { label: 'Order Compliance', bg: 'bg-red-100',    text: 'text-red-700'    },
  'other':            { label: 'Other',            bg: 'bg-slate-100',  text: 'text-slate-600'  },
}

// selectedComms / selectedCal are Sets of indices
function initSelected(arr) {
  return new Set(arr.map((_, i) => i))
}

export default function Import() {
  const navigate = useNavigate()
  const { addComm, addCalendarEvent } = useApp()
  const fileRef = useRef(null)

  const [step,       setStep]       = useState(1)
  const [platform,   setPlatform]   = useState(null)
  const [file,       setFile]       = useState(null)
  const [parsing,    setParsing]    = useState(false)
  const [parseError, setParseError] = useState('')
  const [parsed,     setParsed]     = useState(null)   // { platform, comms[], calendarEvents[], date_range }
  const [selComms,   setSelComms]   = useState(new Set())
  const [selCal,     setSelCal]     = useState(new Set())
  const [saving,     setSaving]     = useState(false)
  const [savedCounts, setSavedCounts] = useState(null) // { comms, cal }

  const platformMeta = PLATFORMS.find(p => p.id === platform)

  function handleFileChange(e) {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setParseError('')
  }

  async function handleParse() {
    if (!file) return
    setParsing(true)
    setParseError('')
    try {
      const result = await dataApi.importPlatform(file)
      setParsed(result)
      setSelComms(initSelected(result.comms))
      setSelCal(initSelected(result.calendarEvents))
      setStep(3)
    } catch (err) {
      setParseError(err.message || 'Failed to parse the file. Please try again.')
    } finally {
      setParsing(false)
    }
  }

  function toggleComm(i) {
    setSelComms(prev => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  function toggleCal(i) {
    setSelCal(prev => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  async function handleSave() {
    if (!parsed) return
    setSaving(true)

    let commsCount = 0
    for (const i of selComms) {
      try { await addComm(parsed.comms[i]); commsCount++ } catch { /* continue */ }
    }

    let calCount = 0
    for (const i of selCal) {
      try { await addCalendarEvent(parsed.calendarEvents[i]); calCount++ } catch { /* continue */ }
    }

    setSavedCounts({ comms: commsCount, cal: calCount })
    setSaving(false)
    setStep(4)
  }

  const totalSelected = selComms.size + selCal.size

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Import Records" showBack />

      <div className="screen no-scrollbar px-4 py-5 space-y-4">

        {/* ── Step 1: Platform picker ── */}
        {step === 1 && (
          <>
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                Which platform are you importing from?
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                We'll walk you through how to export your data.
              </p>
            </div>

            <div className="space-y-3">
              {PLATFORMS.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  className={[
                    'w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all',
                    platform === p.id
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800',
                  ].join(' ')}
                >
                  <span className="text-3xl flex-shrink-0">{p.emoji}</span>
                  <p className="flex-1 font-semibold text-slate-800 dark:text-slate-100 text-sm">{p.name}</p>
                  {platform === p.id && (
                    <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>

            <Button variant="primary" size="lg" fullWidth disabled={!platform} onClick={() => setStep(2)}>
              Continue
            </Button>
          </>
        )}

        {/* ── Step 2: Export instructions + file upload ── */}
        {step === 2 && platformMeta && (
          <>
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">{platformMeta.emoji}</span>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  How to export from {platformMeta.name}
                </h3>
              </div>
              <ol className="space-y-3">
                {platformMeta.instructions.map((inst, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-600 dark:text-slate-300">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span>{inst}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Upload zone */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className={[
                'w-full border-2 border-dashed rounded-2xl p-6 text-center transition-all',
                file
                  ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20'
                  : 'border-slate-300 dark:border-slate-600 hover:border-brand-400 dark:hover:border-brand-500',
              ].join(' ')}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleFileChange}
              />
              {file ? (
                <>
                  <p className="text-2xl mb-1">📄</p>
                  <p className="text-sm font-semibold text-brand-700 dark:text-brand-300 truncate px-4">{file.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {(file.size / 1024 / 1024).toFixed(1)} MB — tap to change
                  </p>
                </>
              ) : (
                <>
                  <p className="text-3xl mb-2">📤</p>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Tap to upload your exported file</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">PDF, JPEG, or PNG · Max 100 MB</p>
                </>
              )}
            </button>

            {parseError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-sm text-red-700 dark:text-red-400">
                {parseError}
              </div>
            )}

            <div className="disclaimer text-xs">
              <strong>Privacy:</strong> Your file is analyzed server-side using AI to extract records. It is not permanently stored.
            </div>

            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={!file}
              loading={parsing}
              onClick={handleParse}
            >
              {parsing ? 'Analyzing file…' : '✨ Extract Records'}
            </Button>
          </>
        )}

        {/* ── Step 3: Preview + select records ── */}
        {step === 3 && parsed && (
          <>
            {/* Summary row */}
            <div className="flex gap-3">
              <div className="flex-1 card p-3 text-center">
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{parsed.comms.length}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Messages</p>
              </div>
              <div className="flex-1 card p-3 text-center">
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{parsed.calendarEvents.length}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Calendar Events</p>
              </div>
            </div>

            {parsed.date_range?.from && (
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                Date range: {parsed.date_range.from} → {parsed.date_range.to || 'present'}
              </p>
            )}

            {/* ── Communications section ── */}
            {parsed.comms.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    💬 Communications ({selComms.size}/{parsed.comms.length} selected)
                  </h3>
                  <div className="flex gap-3">
                    <button onClick={() => setSelComms(initSelected(parsed.comms))}
                      className="text-xs text-brand-600 dark:text-brand-400 font-semibold tap-target">All</button>
                    <button onClick={() => setSelComms(new Set())}
                      className="text-xs text-slate-400 font-semibold tap-target">None</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {parsed.comms.map((rec, i) => {
                    const isOn     = selComms.has(i)
                    const toneMeta = COMM_TONE_META[rec.tone] || COMM_TONE_META.neutral
                    return (
                      <button
                        key={i}
                        onClick={() => toggleComm(i)}
                        className={[
                          'w-full text-left p-4 rounded-2xl border-2 transition-all',
                          isOn
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 opacity-50',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{rec.date}</span>
                              {rec.time && <span className="text-xs text-slate-400">{rec.time}</span>}
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${toneMeta.bg} ${toneMeta.text}`}>
                                {toneMeta.label}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug line-clamp-2">
                              {rec.summary}
                            </p>
                          </div>
                          <Checkbox checked={isOn} />
                        </div>
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {/* ── Calendar Events section ── */}
            {parsed.calendarEvents.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    📅 Calendar Events ({selCal.size}/{parsed.calendarEvents.length} selected)
                  </h3>
                  <div className="flex gap-3">
                    <button onClick={() => setSelCal(initSelected(parsed.calendarEvents))}
                      className="text-xs text-brand-600 dark:text-brand-400 font-semibold tap-target">All</button>
                    <button onClick={() => setSelCal(new Set())}
                      className="text-xs text-slate-400 font-semibold tap-target">None</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {parsed.calendarEvents.map((ev, i) => {
                    const isOn    = selCal.has(i)
                    const calMeta = CAL_TYPE_META[ev.type] || CAL_TYPE_META.other
                    return (
                      <button
                        key={i}
                        onClick={() => toggleCal(i)}
                        className={[
                          'w-full text-left p-4 rounded-2xl border-2 transition-all',
                          isOn
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 opacity-50',
                        ].join(' ')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${calMeta.bg} ${calMeta.text}`}>
                                {calMeta.label}
                              </span>
                              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ''}
                              </span>
                              {ev.start_time && <span className="text-xs text-slate-400">{ev.start_time}</span>}
                            </div>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{ev.title}</p>
                            {ev.location && (
                              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">📍 {ev.location}</p>
                            )}
                            {ev.notes && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{ev.notes}</p>
                            )}
                          </div>
                          <Checkbox checked={isOn} />
                        </div>
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {/* Empty state */}
            {parsed.comms.length === 0 && parsed.calendarEvents.length === 0 && (
              <div className="card p-6 text-center">
                <p className="text-2xl mb-2">🔍</p>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No records found</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Try a different file or check that it contains message history.
                </p>
                <Button variant="ghost" size="sm" className="mt-3" onClick={() => setStep(2)}>
                  Try again
                </Button>
              </div>
            )}

            {totalSelected > 0 && (
              <>
                <div className="disclaimer text-xs">
                  Messages go to your <strong>Communications Log</strong>. Events go to your <strong>Calendar</strong>. You can edit or delete them there.
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving…' : `Import ${totalSelected} record${totalSelected !== 1 ? 's' : ''}`}
                </Button>
              </>
            )}
          </>
        )}

        {/* ── Step 4: Done ── */}
        {step === 4 && savedCounts && (
          <div className="flex flex-col items-center text-center gap-5 py-10">
            <div className="text-6xl">✅</div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Import complete!</h2>
              <div className="mt-3 space-y-1">
                {savedCounts.comms > 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    💬 {savedCounts.comms} message{savedCounts.comms !== 1 ? 's' : ''} added to Communications Log
                  </p>
                )}
                {savedCounts.cal > 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    📅 {savedCounts.cal} event{savedCounts.cal !== 1 ? 's' : ''} added to Calendar
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full">
              {savedCounts.comms > 0 && (
                <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/comms')}>
                  View Communications Log
                </Button>
              )}
              {savedCounts.cal > 0 && (
                <Button variant={savedCounts.comms > 0 ? 'ghost' : 'primary'} size="lg" fullWidth onClick={() => navigate('/calendar')}>
                  View Calendar
                </Button>
              )}
              <Button variant="ghost" size="md" fullWidth onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}

        <div className="pb-4" />
      </div>
    </div>
  )
}

// ── Small checkbox indicator ───────────────────────────────────────────────────
function Checkbox({ checked }) {
  return (
    <div className={[
      'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
      checked ? 'border-brand-500 bg-brand-500' : 'border-slate-300 dark:border-slate-600',
    ].join(' ')}>
      {checked && (
        <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  )
}
